class SwitchModuleNavigation {
  constructor(container) {
    this.container = container;
    this.levelStack = ['0'];
    this.pdpLookupData = null;
    this.fallbackSelection = null;

    const modal = this.container.closest('.switch-module');
    this.defaultTitles = modal ? Array.from(modal.querySelectorAll('.switch-module__text > :not(.level-two):not(.level-fallback)')) : [];
    this.subTitles = modal ? Array.from(modal.querySelectorAll('.switch-module__text .level-two')) : [];
    this.fallbackTitles = modal ? Array.from(modal.querySelectorAll('.switch-module__text .level-fallback')) : [];
    this.fallbackImageWrap = this.container.querySelector('[data-fallback-image-wrap]');
    this.fallbackImage = this.container.querySelector('[data-fallback-image]');
    this.fallbackModel = this.container.querySelector('[data-fallback-model]');
    this.fallbackText = this.container.querySelector('[data-fallback-text]');
    this.fallbackButtonWrap = this.container.querySelector('[data-fallback-button-wrap]');
    this.fallbackButton = this.fallbackButtonWrap ? this.fallbackButtonWrap.querySelector('.button') : null;
    this.fallbackTitleTemplates = new Map(this.fallbackTitles.map((el) => [el, el.innerHTML]));
    this.fallbackTextTemplate = this.fallbackText ? this.fallbackText.innerHTML : '';
    this.fallbackButtonTemplate = this.fallbackButton ? this.fallbackButton.innerHTML : '';
    this.pdpContextNode = this.container.querySelector('[data-pdp-context]');
    this.isPdp = this.pdpContextNode ? this.pdpContextNode.dataset.isPdp === 'true' : false;
    this.pdpVariantSearch = this.pdpContextNode ? (this.pdpContextNode.dataset.pdpVariantSearch || '') : '';
    this.pdpLookupUrl = this.pdpContextNode ? (this.pdpContextNode.dataset.pdpLookupUrl || '') : '';
    this.pdpLookupDataPromise = null;
    this.filterSourceCollectionUrl = this.pdpContextNode ? (this.pdpContextNode.dataset.filterSourceCollectionUrl || '') : '';
    this.filterSourceCollectionId = '492690047252';
    this.filterSourceCollectionUrlPromise = null;
    this.filterNoMatchMessage = modal ? modal.querySelector('[data-filter-no-match-message]') : null;
    this.filterNoMatchModelNodes = this.filterNoMatchMessage
      ? Array.from(this.filterNoMatchMessage.querySelectorAll('[data-filter-no-match-model]'))
      : [];
    this.filterNoMatchLink = this.filterNoMatchMessage
      ? this.filterNoMatchMessage.querySelector('[data-filter-no-match-link]')
      : null;

    this.init();
  }

  init() {
    this.container.querySelectorAll('[data-submenu-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.showSubmenu(trigger));
    });

    this.container.querySelectorAll('[data-back-button]').forEach((button) => {
      button.addEventListener('click', () => this.goBack());
    });

    this.initLinkListeners();
    this.initFilterButtonListeners();
    this.initFallbackButtonListener();
    this.preloadPdpLookupData();
  }

  getPdpLookupCacheKey() {
    if (!this.pdpLookupUrl || !this.pdpVariantSearch) return '';

    return `switch-module-pdp-lookup:${encodeURIComponent(this.pdpLookupUrl)}:${encodeURIComponent(this.pdpVariantSearch)}`;
  }

  readPdpLookupDataFromCache() {
    const cacheKey = this.getPdpLookupCacheKey();
    if (!cacheKey) return null;

    try {
      const rawValue = window.sessionStorage.getItem(cacheKey);
      if (!rawValue) return null;

      const parsedValue = JSON.parse(rawValue);
      const expiresAt = parsedValue && Number(parsedValue.expiresAt);
      const data = parsedValue && parsedValue.data;

      if (!Array.isArray(data)) return null;
      if (!expiresAt || expiresAt <= Date.now()) {
        window.sessionStorage.removeItem(cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  }

  writePdpLookupDataToCache(data) {
    const cacheKey = this.getPdpLookupCacheKey();
    if (!cacheKey || !Array.isArray(data)) return;

    try {
      const cacheEntry = {
        expiresAt: Date.now() + (10 * 60 * 1000),
        data
      };

      window.sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      // Ignore cache write failures.
    }
  }

  preloadPdpLookupData() {
    if (!this.isPdp || !this.pdpVariantSearch || !this.pdpLookupUrl) return;

    const preload = () => {
      this.getPdpLookupData().catch(() => {
        // Ignore preload failures; normal flow will retry on demand.
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(preload, { timeout: 1500 });
      return;
    }

    window.setTimeout(preload, 0);
  }

  initFallbackButtonListener() {
    if (!this.fallbackButton) return;

    this.fallbackButton.addEventListener('click', (event) => {
      event.preventDefault();

      if (!this.fallbackSelection || !this.fallbackSelection.url) return;

      const { brand, model, url } = this.fallbackSelection;
      this.saveToCart({ brand, model, url }, url, null, true);
    });
  }

  escapeHtml(value) {
    return (value || '').toString().replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[character] || character));
  }

  getCurrentBrandName() {
    const currentBrandLevel = this.levelStack.find((levelId, index) => index > 0 && levelId.startsWith('brand_'));
    if (!currentBrandLevel) return '';

    return currentBrandLevel
      .replace('brand_', '')
      .split(/[-_]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  getFallbackBrandName(link) {
    const linkBrandName = link && link.dataset ? (link.dataset.brandName || '').trim() : '';
    if (linkBrandName) return linkBrandName;

    return this.getCurrentBrandName();
  }

  buildFallbackDisplayModel(brandName, modelName) {
    const cleanBrandName = (brandName || '').trim();
    const cleanModelName = (modelName || '').trim();

    if (!cleanBrandName) return cleanModelName;
    if (!cleanModelName) return cleanBrandName;

    const normalizedBrandName = this.normalizeText(cleanBrandName).replace(/[-_]+/g, ' ');
    const normalizedModelName = this.normalizeText(cleanModelName).replace(/[-_]+/g, ' ');

    if (normalizedModelName.includes(normalizedBrandName)) {
      return cleanModelName;
    }

    return `${cleanBrandName} ${cleanModelName}`;
  }

  renderFallbackTemplate(template, brandName, modelName) {
    if (!template) return '';

    const displayModelName = this.buildFallbackDisplayModel(brandName, modelName);
    const modelReplacement = template.includes('{brand}') ? modelName : displayModelName;

    return template
      .replace(/\{brand\}/g, this.escapeHtml(brandName))
      .replace(/\{model\}/g, this.escapeHtml(modelReplacement));
  }

  syncFallbackTemplates(brandName, modelName) {
    const hasFallbackContext = !!((brandName || '').trim() || (modelName || '').trim());

    this.fallbackTitleTemplates.forEach((template, element) => {
      element.innerHTML = hasFallbackContext ? this.renderFallbackTemplate(template, brandName, modelName) : template;
    });

    if (this.fallbackText) {
      this.fallbackText.innerHTML = hasFallbackContext ? this.renderFallbackTemplate(this.fallbackTextTemplate, brandName, modelName) : this.fallbackTextTemplate;
    }

    if (this.fallbackButton) {
      this.fallbackButton.innerHTML = hasFallbackContext ? this.renderFallbackTemplate(this.fallbackButtonTemplate, brandName, modelName) : this.fallbackButtonTemplate;
    }
  }

  initLinkListeners() {
    this.container.querySelectorAll('a.switch-module__item-button').forEach((link) => {
      link.addEventListener('click', async (e) => {
        e.preventDefault();
        const fallbackUrl = link.getAttribute('href');
        const url = await this.resolvePdpRedirectUrl(fallbackUrl);
        const model = link.textContent.trim();
        const brand = link.dataset.brandName;

        if (url === fallbackUrl && this.isPdp) {
          this.updateFallbackContent(link, { brand, model, url: fallbackUrl });
          this.showSubmenu(false, 'fallback');
          return;
        }

        this.saveToCart({ brand, model, url: fallbackUrl }, url, null, true);
      });
    });
  }

  normalizePath(url) {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.replace(/\/+$/, '') || '/';
    } catch (error) {
      return '';
    }
  }

  async getFilterSourceCollectionUrl() {
    if (this.filterSourceCollectionUrl) {
      return this.filterSourceCollectionUrl;
    }

    if (this.filterSourceCollectionUrlPromise) return this.filterSourceCollectionUrlPromise;

    this.filterSourceCollectionUrlPromise = (async () => {
      const collectionHandle = await this.findCollectionHandleById(this.filterSourceCollectionId);
      if (!collectionHandle) return '';

      return `/collections/${collectionHandle}`;
    })();

    this.filterSourceCollectionUrlPromise = this.filterSourceCollectionUrlPromise.then((collectionUrl) => {
      this.filterSourceCollectionUrl = collectionUrl || '';
      return this.filterSourceCollectionUrl;
    });

    return this.filterSourceCollectionUrlPromise;
  }

  async findCollectionHandleById(collectionId) {
    const normalizedCollectionId = String(collectionId || '').trim();
    if (!normalizedCollectionId) return '';

    const pageSize = 250;
    let page = 1;

    while (page <= 10) {
      try {
        const response = await fetch(`/collections.json?limit=${pageSize}&page=${page}`, { credentials: 'same-origin' });
        if (!response.ok) return '';

        const payload = await response.json();
        const collections = Array.isArray(payload && payload.collections) ? payload.collections : [];
        if (!collections.length) return '';

        const matchingCollection = collections.find((collection) => String(collection.id) === normalizedCollectionId);
        if (matchingCollection && matchingCollection.handle) {
          return matchingCollection.handle;
        }

        if (collections.length < pageSize) return '';

        page += 1;
      } catch (error) {
        return '';
      }
    }

    return '';
  }

  async getSectionRenderedFilterScope() {
    const collectionUrl = await this.getFilterSourceCollectionUrl();
    if (!collectionUrl) return null;

    const requestUrls = [
      `${collectionUrl}?section_id=main-collection`,
      `${collectionUrl}?section_id=main`,
      collectionUrl
    ];

    for (const requestUrl of requestUrls) {
      try {
        const response = await fetch(requestUrl, { credentials: 'same-origin' });
        if (!response.ok) continue;

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const hasFilterInputs = parsedDocument.querySelector('form[is="facet-form"] input[type="checkbox"][name="filter.p.m.custom.collection_filter_suitable_for"]');

        if (hasFilterInputs) {
          return parsedDocument;
        }
      } catch (error) {
        continue;
      }
    }

    return null;
  }

  extractJsonArray(text) {
    if (!text) return null;

    const trimmed = text.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) return trimmed;

    const start = trimmed.indexOf('[');
    const end = trimmed.lastIndexOf(']');
    if (start === -1 || end === -1 || end <= start) return null;

    return trimmed.slice(start, end + 1);
  }

  async getPdpLookupData() {
    if (!this.isPdp || !this.pdpVariantSearch || !this.pdpLookupUrl) return [];
    if (Array.isArray(this.pdpLookupData)) return this.pdpLookupData;

    const cachedData = this.readPdpLookupDataFromCache();
    if (Array.isArray(cachedData)) {
      this.pdpLookupData = cachedData;
      return this.pdpLookupData;
    }

    if (this.pdpLookupDataPromise) {
      return this.pdpLookupDataPromise;
    }

    this.pdpLookupDataPromise = (async () => {
      try {
        const response = await fetch(this.pdpLookupUrl, { credentials: 'same-origin' });
        if (!response.ok) {
          this.pdpLookupData = [];
          return this.pdpLookupData;
        }

        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        let parsed = null;

        if (contentType.includes('application/json')) {
          parsed = await response.json();
        } else {
          const responseText = await response.text();
          const jsonArrayText = this.extractJsonArray(responseText);
          parsed = jsonArrayText ? JSON.parse(jsonArrayText) : [];
        }

        this.pdpLookupData = Array.isArray(parsed) ? parsed : [];
        this.writePdpLookupDataToCache(this.pdpLookupData);
        return this.pdpLookupData;
      } catch (error) {
        this.pdpLookupData = [];
        return this.pdpLookupData;
      } finally {
        this.pdpLookupDataPromise = null;
      }
    })();

    return this.pdpLookupDataPromise;
  }

  async resolvePdpRedirectUrl(clickedUrl) {
    if (!this.isPdp || !clickedUrl) return clickedUrl;

    const clickedPath = this.normalizePath(clickedUrl);
    if (!clickedPath) return clickedUrl;

    const lookupData = await this.getPdpLookupData();

    const matchingItem = lookupData.find((item) => this.normalizePath(item.collection_url) === clickedPath);

    if (matchingItem && matchingItem.product_url) {
      return matchingItem.product_url;
    }

    return clickedUrl;
  }

  initFilterButtonListeners() {
    this.container.querySelectorAll('button[data-filter-link-button][data-link-title]').forEach((button) => {
      button.addEventListener('click', () => {
        const linkTitle = button.dataset.linkTitle;
        const matchResult = this.findMatchingFilterInput(linkTitle);
        const matchingInput = matchResult.input;

        this.hideFilterNoMatchMessage();

        if (!matchingInput) {
          if (matchResult.error === 'multiple_exact' || matchResult.error === 'multiple_suffix') {
            window.alert(`Er zijn meerdere filters gevonden voor "${linkTitle}" (${matchResult.count}). Selectie is daarom niet automatisch toegepast.`);
            return;
          }

          if (this.showFilterNoMatchMessage(linkTitle, button.dataset.collectionUrl)) return;

          window.alert(`Geen matchend filter gevonden voor "${linkTitle}".`);
          return;
        }

        const model = (linkTitle || '').trim();
        const brand = button.dataset.brandName;
        const url = window.location.href;

        this.saveToCart({ brand, model, url }, null, () => {
          this.activateFilterInput(matchingInput);
        });
      });
    });
  }

  showFilterNoMatchMessage(modelName, collectionUrl) {
    if (!this.filterNoMatchMessage) return false;

    this.filterNoMatchModelNodes.forEach((node) => {
      node.textContent = (modelName || '').trim();
    });
    if (this.filterNoMatchLink && collectionUrl) {
      this.filterNoMatchLink.setAttribute('href', collectionUrl);
    }
    this.filterNoMatchMessage.hidden = false;
    this.filterNoMatchMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    return true;
  }

  hideFilterNoMatchMessage() {
    if (this.filterNoMatchMessage) {
      this.filterNoMatchMessage.hidden = true;
    }
  }

  normalizeText(value) {
    return (value || '').toString().toLowerCase().trim().replace(/\s+/g, ' ');
  }

  normalizeFilterLabel(value) {
    return this.normalizeText(value).replace(/\s*\(\d+\)\s*$/, '');
  }

  normalizeFilterMatchValue(value) {
    return this.normalizeFilterLabel(value)
      .replace(/[()]/g, ' ')
      .replace(/\s*[-–—]\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/(\d)\s+([a-z]+)/gi, '$1$2')
      .trim();
  }

  getInputComparableData(input, scope = document) {
    const normalizedValue = this.normalizeText(input.value);
    const queryScope = scope && typeof scope.querySelector === 'function' ? scope : document;
    const label = input.id ? queryScope.querySelector(`label[for="${input.id}"]`) : null;
    const normalizedLabel = this.normalizeFilterLabel(label ? label.textContent : '');

    const normalizedMatchValue = this.normalizeFilterMatchValue(normalizedValue);
    const normalizedMatchLabel = this.normalizeFilterMatchValue(normalizedLabel);

    return { normalizedValue, normalizedLabel, normalizedMatchValue, normalizedMatchLabel };
  }

  isInputVisible(input) {
    return !!(input.offsetParent || input.getClientRects().length);
  }

  pickBestInput(inputs) {
    const enabledInputs = inputs.filter((input) => !input.disabled);
    const visibleEnabledInputs = enabledInputs.filter((input) => this.isInputVisible(input));
    const visibleInputs = inputs.filter((input) => this.isInputVisible(input));

    return visibleEnabledInputs[0] || enabledInputs[0] || visibleInputs[0] || inputs[0] || null;
  }

  dedupeInputsBySignature(inputs, scope = document) {
    const grouped = new Map();

    inputs.forEach((input) => {
      const {
        normalizedValue,
        normalizedLabel,
        normalizedMatchValue,
        normalizedMatchLabel
      } = this.getInputComparableData(input, scope);

      const signature = normalizedMatchValue || normalizedMatchLabel || normalizedValue || normalizedLabel;
      if (!signature) return;

      if (!grouped.has(signature)) grouped.set(signature, []);
      grouped.get(signature).push(input);
    });

    return Array.from(grouped.values()).map((group) => this.pickBestInput(group)).filter(Boolean);
  }

  findMatchingFilterInput(linkTitle, scope = document) {
    const normalizedTitle = this.normalizeText(linkTitle);
    const normalizedMatchTitle = this.normalizeFilterMatchValue(linkTitle);
    if (!normalizedTitle && !normalizedMatchTitle) return { input: null, error: 'not_found', count: 0 };

    const queryScope = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    const filterInputs = Array.from(queryScope.querySelectorAll('form[is="facet-form"] input[type="checkbox"][name="filter.p.m.custom.collection_filter_suitable_for"]'));

    const findExactMatch = (input) => {
      const {
        normalizedValue,
        normalizedLabel,
        normalizedMatchValue,
        normalizedMatchLabel
      } = this.getInputComparableData(input, queryScope);

      if (normalizedTitle && normalizedValue === normalizedTitle) return true;
      if (normalizedTitle && normalizedLabel === normalizedTitle) return true;

      if (normalizedMatchTitle && normalizedMatchValue === normalizedMatchTitle) return true;

      return normalizedMatchTitle && normalizedMatchLabel === normalizedMatchTitle;
    };

    const exactMatches = this.dedupeInputsBySignature(filterInputs.filter(findExactMatch), queryScope);
    if (exactMatches.length === 1) {
      return { input: exactMatches[0], error: null, count: 1 };

    }

    if (exactMatches.length > 1) {
      return { input: null, error: 'multiple_exact', count: exactMatches.length };
    }


    const suffixMatches = this.dedupeInputsBySignature(filterInputs.filter((input) => {
      const {
        normalizedValue,
        normalizedLabel,
        normalizedMatchValue,
        normalizedMatchLabel
      } = this.getInputComparableData(input, queryScope);

      if (normalizedTitle && normalizedValue.endsWith(normalizedTitle)) return true;
      if (normalizedTitle && normalizedLabel.endsWith(normalizedTitle)) return true;

      if (normalizedMatchTitle && normalizedMatchValue.endsWith(normalizedMatchTitle)) return true;

      return normalizedMatchTitle && normalizedMatchLabel.endsWith(normalizedMatchTitle);
    }), queryScope);

    if (suffixMatches.length === 1) {
      return { input: suffixMatches[0], error: null, count: 1 };
    }

    if (suffixMatches.length > 1) {
      return { input: null, error: 'multiple_suffix', count: suffixMatches.length };
    }

    return { input: null, error: 'not_found', count: 0 };
  }

  getSelectionFilterLookupTitles(selection) {
    const model = (selection && selection.model ? selection.model : '').trim();
    const brand = (selection && selection.brand ? selection.brand : '').trim();
    const titles = [];

    const addTitle = (title) => {
      const normalizedTitle = this.normalizeText(title);
      if (!normalizedTitle) return;

      if (titles.some((existingTitle) => this.normalizeText(existingTitle) === normalizedTitle)) {
        return;
      }

      titles.push(title.trim());
    };

    addTitle(model);
    addTitle(this.buildFallbackDisplayModel(brand, model));
    addTitle(`${brand} ${model}`);

    return titles;
  }

  async resolveSectionRenderedFilterContent(selection) {
    const filterScope = await this.getSectionRenderedFilterScope();
    if (!filterScope) return '';

    const lookupTitles = this.getSelectionFilterLookupTitles(selection);

    for (const lookupTitle of lookupTitles) {
      const matchResult = this.findMatchingFilterInput(lookupTitle, filterScope);
      if (matchResult && matchResult.input) {
        return encodeURIComponent(matchResult.input.value || '');
      }
    }

    return '';
  }

  buildDefaultFilterContent(selection) {
    return 'false'
  }

  activateFilterInput(input) {
    const form = input.closest('form');
    if (!form) return;

    const sameFilterInputs = Array.from(form.querySelectorAll('input[type="checkbox"][name="filter.p.m.custom.collection_filter_suitable_for"]'));

    sameFilterInputs.forEach((filterInput) => {
      if (filterInput !== input && filterInput.checked) {
        filterInput.checked = false;
        filterInput.dispatchEvent(new Event('input', { bubbles: true }));
        filterInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const action = form.getAttribute('action') || window.location.pathname;
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const query = searchParams.toString();
    const targetUrl = query ? `${action}?${query}` : action;

    window.location.href = targetUrl;
  }

  async saveToCart(selection, redirectUrl, onComplete, resolveFilterFromSection = true) {
    const defaultFilterContent = this.buildDefaultFilterContent(selection);
    let filterContent = defaultFilterContent;

    if (resolveFilterFromSection) {
      try {
        filterContent = await this.resolveSectionRenderedFilterContent(selection);
      } catch (error) {
        filterContent = '';
      }
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'switch-module-brand': selection.brand,
          'switch-module-model': selection.model,
          'switch-module-url': selection.url,
          'switch-module-filter': filterContent,
          "DeviceID": "Hier moet de Modelcode komen deze moet uiteindelijk ook door naar de line items zodat het meegestuurd wordt bij aankoop"
        }
      })
    })
    .finally(() => {
      if (typeof onComplete === 'function') {
        onComplete();
        return;
      }

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    });
  }

  updateFallbackContent(link, selection = null) {
    const modelText = selection && selection.model ? selection.model : (link ? link.textContent.trim() : '');
    const brandText = this.getFallbackBrandName(link);
    const displayModelText = this.buildFallbackDisplayModel(brandText, modelText);
    const imageUrl = link && link.dataset ? (link.dataset.collectionImage || '') : '';

    if (selection && selection.url) {
      this.fallbackSelection = {
        brand: selection.brand || (this.levelStack[1] || '').replace('brand_', ''),
        model: modelText,
        url: selection.url
      };
    } else if (link) {
      const fallbackUrl = link.getAttribute('href') || '';
      this.fallbackSelection = fallbackUrl
        ? {
            brand: (this.levelStack[1] || '').replace('brand_', ''),
            model: modelText,
            url: fallbackUrl
          }
        : null;
    } else {
      this.fallbackSelection = null;
    }

    this.syncFallbackTemplates(brandText, modelText);

    if (this.fallbackModel) {
      this.fallbackModel.textContent = displayModelText;
      this.fallbackModel.hidden = !displayModelText;
    }

    if (!this.fallbackImageWrap || !this.fallbackImage) return;

    if (imageUrl) {
      this.fallbackImage.src = imageUrl;
      this.fallbackImage.alt = displayModelText;
      this.fallbackImageWrap.hidden = false;
      return;
    }

    this.fallbackImage.removeAttribute('src');
    this.fallbackImage.alt = '';
    this.fallbackImageWrap.hidden = true;
  }

  showSubmenu(trigger, manualTrigger = false) {
    let submenuId = '';
    if (!trigger && manualTrigger) {
      submenuId = manualTrigger;
    } else {
      submenuId = trigger.dataset.submenuTrigger;
    }
    
    const targetLevel = this.container.querySelector(`[data-menu-level="${submenuId}"]`);
    if (!targetLevel) return;

    this.currentLevel().hidden = true;
    targetLevel.hidden = false;
    this.levelStack.push(submenuId);
    this.updateTitles();
  }

  goBack() {
    if (this.levelStack.length <= 1) return;

    this.currentLevel().hidden = true;
    this.levelStack.pop();
    this.currentLevel().hidden = false;
    this.updateTitles();
  }

  updateTitles() {
    const currentLevelId = this.levelStack[this.levelStack.length - 1];
    const isRoot = currentLevelId === '0';
    const isFallback = currentLevelId === 'fallback';

    this.defaultTitles.forEach(el => el.classList.toggle('hidden', !isRoot));
    this.subTitles.forEach(el => el.classList.toggle('hidden', isRoot || isFallback));
    this.fallbackTitles.forEach(el => el.classList.toggle('hidden', !isFallback));
  }

  currentLevel() {
    return this.container.querySelector(`[data-menu-level="${this.levelStack[this.levelStack.length - 1]}"]`);
  }

  reset() {
    // Hide all non-root levels and return to level 0
    this.container.querySelectorAll('[data-menu-level]').forEach((level) => {
      level.hidden = level.dataset.menuLevel !== '0';
    });
    this.levelStack = ['0'];
    this.updateFallbackContent(null);
    this.updateTitles();
  }
}

function normalizeSwitchModuleCollectionHandle(value) {
  return (value || '').toString().trim().toLowerCase();
}

const SWITCH_MODULE_FILTER_PARAM = 'filter.p.m.custom.collection_filter_suitable_for';
const SWITCH_MODULE_LEGACY_FILTER_PARAM = 'filter.p.m.custom.brand_collection_filter';
const SWITCH_MODULE_REDIRECT_SUPPRESSION_STORAGE_KEY = 'switch-module-redirect-suppression';
const SWITCH_MODULE_GLOBAL_SUPPRESSION_STORAGE_KEY = 'switch-module-redirect-global-suppression';
const SWITCH_MODULE_HISTORY_PATCHED_FLAG = '__switchModuleHistoryPatched';
const SWITCH_MODULE_LAST_URL_STORAGE_KEY = 'switch-module-last-url';

function decodeSwitchModuleFilterValue(value) {
  const stringValue = (value || '').toString().trim();
  if (!stringValue) return '';

  try {
    return decodeURIComponent(stringValue);
  } catch (error) {
    return stringValue;
  }
}

// replace url functionality

function normalizeSwitchModuleFilterValue(value) {
  return decodeSwitchModuleFilterValue(value).toLowerCase().trim().replace(/\s+/g, ' ');
}

function getParsedSwitchModuleUrl(url) {
  if (!url) return null;

  try {
    return new URL(url, window.location.origin);
  } catch (error) {
    return null;
  }
}

function normalizeSwitchModulePathname(pathname) {
  const normalizedPathname = (pathname || '').toString().trim().replace(/\/+$/, '');
  return normalizedPathname || '/';
}

function getSwitchModuleNormalizedSearch(searchParams) {
  if (!searchParams) return '';

  const entries = Array.from(searchParams.entries())
    .filter(([key]) => key !== SWITCH_MODULE_FILTER_PARAM && key !== SWITCH_MODULE_LEGACY_FILTER_PARAM)
    .sort(([leftKey, leftValue], [rightKey, rightValue]) => {
      if (leftKey === rightKey) return leftValue.localeCompare(rightValue);
      return leftKey.localeCompare(rightKey);
    });

  if (!entries.length) return '';

  const normalizedSearchParams = new URLSearchParams();
  entries.forEach(([key, value]) => normalizedSearchParams.append(key, value));

  const normalizedSearch = normalizedSearchParams.toString();
  return normalizedSearch ? `?${normalizedSearch}` : '';
}

function getSwitchModuleRouteKeyFromParsedUrl(parsedUrl) {
  if (!parsedUrl) return '';

  const pathname = normalizeSwitchModulePathname(parsedUrl.pathname);
  const search = getSwitchModuleNormalizedSearch(parsedUrl.searchParams);

  return `${pathname}${search}`;
}

function getSwitchModuleRouteKey(url) {
  return getSwitchModuleRouteKeyFromParsedUrl(getParsedSwitchModuleUrl(url));
}

function getSwitchModuleCollectionContext() {
  const rawCollectionHandles = Array.isArray(window.collectionHandles) ? window.collectionHandles : [];
  const switchFilter = decodeSwitchModuleFilterValue(window.switchFilter);

  if (!rawCollectionHandles.length || !switchFilter || switchFilter === 'false') return null;

  const collectionHandles = new Set();
  const managedUrlTargets = new Set();

  rawCollectionHandles.forEach((entry) => {
    const stringEntry = (entry || '').toString().trim();
    const looksLikeUrl = stringEntry.includes('://') || stringEntry.startsWith('/');

    if (looksLikeUrl) {
      const parsedUrl = getParsedSwitchModuleUrl(stringEntry);
      if (parsedUrl) {
        const routeKey = getSwitchModuleRouteKeyFromParsedUrl(parsedUrl);
        if (routeKey) managedUrlTargets.add(routeKey);

        const collectionHandle = getCollectionHandleFromParsedUrl(parsedUrl);
        if (collectionHandle) collectionHandles.add(collectionHandle);
        return;
      }
    }

    const collectionHandle = normalizeSwitchModuleCollectionHandle(stringEntry);
    if (collectionHandle) collectionHandles.add(collectionHandle);
  });

  if (!collectionHandles.size && !managedUrlTargets.size) return null;

  return {
    collectionHandles,
    managedUrlTargets,
    switchFilter,
    normalizedSwitchFilter: normalizeSwitchModuleFilterValue(switchFilter)
  };
}

function readSwitchModuleGlobalSuppressions() {
  try {
    const rawValue = window.sessionStorage.getItem(SWITCH_MODULE_GLOBAL_SUPPRESSION_STORAGE_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) : [];

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
}

function writeSwitchModuleGlobalSuppressions(suppressions) {
  try {
    window.sessionStorage.setItem(
      SWITCH_MODULE_GLOBAL_SUPPRESSION_STORAGE_KEY,
      JSON.stringify(Array.isArray(suppressions) ? suppressions : [])
    );
  } catch (error) {
    // Ignore storage write failures.
  }
}

function isSwitchModuleRedirectGloballySuppressed(filterValue) {
  const normalizedFilter = normalizeSwitchModuleFilterValue(filterValue);
  if (!normalizedFilter) return false;

  return readSwitchModuleGlobalSuppressions().includes(normalizedFilter);
}

function suppressSwitchModuleRedirectGlobally(filterValue, reason = '') {
  const normalizedFilter = normalizeSwitchModuleFilterValue(filterValue);
  if (!normalizedFilter) return;

  const suppressions = readSwitchModuleGlobalSuppressions();
  if (suppressions.includes(normalizedFilter)) return;

  suppressions.push(normalizedFilter);
  writeSwitchModuleGlobalSuppressions(suppressions);
}

function clearSwitchModuleGlobalSuppression(filterValue, reason = '') {
  const normalizedFilter = normalizeSwitchModuleFilterValue(filterValue);
  if (!normalizedFilter) return;

  const suppressions = readSwitchModuleGlobalSuppressions();
  const filteredSuppressions = suppressions.filter((entry) => entry !== normalizedFilter);

  if (filteredSuppressions.length === suppressions.length) return;

  writeSwitchModuleGlobalSuppressions(filteredSuppressions);
}

function getSwitchModuleRedirectSuppressionKey(routeKey, filterValue) {
  return `${routeKey || '/'}::${normalizeSwitchModuleFilterValue(filterValue)}`;
}

function readSwitchModuleRedirectSuppressions() {
  try {
    const rawValue = window.sessionStorage.getItem(SWITCH_MODULE_REDIRECT_SUPPRESSION_STORAGE_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) : [];

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
}

function writeSwitchModuleRedirectSuppressions(suppressions) {
  try {
    window.sessionStorage.setItem(
      SWITCH_MODULE_REDIRECT_SUPPRESSION_STORAGE_KEY,
      JSON.stringify(Array.isArray(suppressions) ? suppressions : [])
    );
  } catch (error) {
    // Ignore storage write failures.
  }
}

function suppressSwitchModuleRedirect(routeKey, filterValue) {
  const suppressionKey = getSwitchModuleRedirectSuppressionKey(routeKey, filterValue);
  const suppressions = readSwitchModuleRedirectSuppressions();

  if (suppressions.includes(suppressionKey)) return;

  suppressions.push(suppressionKey);
  writeSwitchModuleRedirectSuppressions(suppressions);
}

function clearSwitchModuleRedirectSuppression(routeKey, filterValue) {
  const suppressionKey = getSwitchModuleRedirectSuppressionKey(routeKey, filterValue);
  const suppressions = readSwitchModuleRedirectSuppressions();
  const filteredSuppressions = suppressions.filter((entry) => entry !== suppressionKey);

  if (filteredSuppressions.length === suppressions.length) return;

  writeSwitchModuleRedirectSuppressions(filteredSuppressions);
}

function isSwitchModuleRedirectSuppressed(routeKey, filterValue) {
  const suppressionKey = getSwitchModuleRedirectSuppressionKey(routeKey, filterValue);
  return readSwitchModuleRedirectSuppressions().includes(suppressionKey);
}

function getSameOriginUrl(url) {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) return null;

    return parsedUrl;
  } catch (error) {
    return null;
  }
}

function readSwitchModuleLastUrl() {
  try {
    const lastUrl = window.sessionStorage.getItem(SWITCH_MODULE_LAST_URL_STORAGE_KEY);
    return getSameOriginUrl(lastUrl);
  } catch (error) {
    return null;
  }
}

function writeSwitchModuleLastUrl(url) {
  try {
    window.sessionStorage.setItem(SWITCH_MODULE_LAST_URL_STORAGE_KEY, url || window.location.href);
  } catch (error) {
    // Ignore storage write failures.
  }
}

function getSwitchModuleCollectionHandleFromParsedUrl(parsedUrl) {
  if (!parsedUrl) return '';

  return getCollectionHandleFromUrl(parsedUrl.href || `${parsedUrl.pathname || '/'}${parsedUrl.search || ''}${parsedUrl.hash || ''}`);
}

function isManagedSwitchModuleUrl(parsedUrl, context) {
  if (!parsedUrl || !context) return false;

  const collectionHandle = getSwitchModuleCollectionHandleFromParsedUrl(parsedUrl);
  if (collectionHandle && context.collectionHandles.has(collectionHandle)) return true;

  const routeKey = getSwitchModuleRouteKeyFromParsedUrl(parsedUrl);
  return !!(routeKey && context.managedUrlTargets && context.managedUrlTargets.has(routeKey));
}

function hasMatchingSwitchFilterParam(parsedUrl, normalizedSwitchFilter) {
  if (!parsedUrl || !normalizedSwitchFilter) return false;

  const currentFilter = parsedUrl.searchParams.get(SWITCH_MODULE_FILTER_PARAM);
  const normalizedCurrentFilter = normalizeSwitchModuleFilterValue(currentFilter);
  return normalizedCurrentFilter === normalizedSwitchFilter;
}

function syncGlobalSwitchModuleRedirectSuppression(currentUrl, context, previousUrl = null) {
  if (!currentUrl || !context) return;

  if (!isManagedSwitchModuleUrl(currentUrl, context)) return;

  const hasMatchingFilterNow = hasMatchingSwitchFilterParam(currentUrl, context.normalizedSwitchFilter);

  if (hasMatchingFilterNow) {
    clearSwitchModuleGlobalSuppression(context.switchFilter, 'matching filter present on current URL');
    return;
  }

  if (previousUrl) {
    const wasManagedCollection = isManagedSwitchModuleUrl(previousUrl, context);
    const hadMatchingFilterBefore = hasMatchingSwitchFilterParam(previousUrl, context.normalizedSwitchFilter);

    if (wasManagedCollection && hadMatchingFilterBefore) {
      suppressSwitchModuleRedirectGlobally(context.switchFilter, 'filter removed from managed collection URL');
      return;
    }
  }

  const referrerUrl = getSameOriginUrl(document.referrer);
  if (!referrerUrl) return;

  const referrerManagedCollection = isManagedSwitchModuleUrl(referrerUrl, context);
  const referrerHadMatchingFilter = hasMatchingSwitchFilterParam(referrerUrl, context.normalizedSwitchFilter);

  if (referrerManagedCollection && referrerHadMatchingFilter) {
    suppressSwitchModuleRedirectGlobally(context.switchFilter, 'arrived without filter after having it on previous page');
  }
}

function installSwitchModuleRedirectSuppressionObservers(context) {
  if (!context) return;

  let lastKnownUrl = new URL(window.location.href);

  const syncFromCurrentUrl = () => {
    const currentUrl = new URL(window.location.href);
    syncGlobalSwitchModuleRedirectSuppression(currentUrl, context, lastKnownUrl);
    lastKnownUrl = currentUrl;
  };

  document.addEventListener('facet:updated', syncFromCurrentUrl);
  window.addEventListener('popstate', syncFromCurrentUrl);

  if (!window[SWITCH_MODULE_HISTORY_PATCHED_FLAG]) {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = function (...args) {
      const result = originalPushState(...args);
      window.dispatchEvent(new Event('switch-module:url-changed'));
      return result;
    };

    window.history.replaceState = function (...args) {
      const result = originalReplaceState(...args);
      window.dispatchEvent(new Event('switch-module:url-changed'));
      return result;
    };

    window[SWITCH_MODULE_HISTORY_PATCHED_FLAG] = true;
  }

  window.addEventListener('switch-module:url-changed', syncFromCurrentUrl);

  const persistCurrentUrl = () => {
    writeSwitchModuleLastUrl(window.location.href);
  };

  window.addEventListener('pagehide', persistCurrentUrl);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persistCurrentUrl();
  });
}

function syncSwitchModuleRedirectSuppression(currentUrl, switchFilter) {
  if (!currentUrl || !switchFilter) return;

  const currentRouteKey = getSwitchModuleRouteKeyFromParsedUrl(currentUrl);
  const currentFilter = currentUrl.searchParams.get(SWITCH_MODULE_FILTER_PARAM);
  const normalizedCurrentFilter = normalizeSwitchModuleFilterValue(currentFilter);
  const normalizedSwitchFilter = normalizeSwitchModuleFilterValue(switchFilter);

  if (normalizedCurrentFilter && normalizedCurrentFilter === normalizedSwitchFilter) {
    clearSwitchModuleRedirectSuppression(currentRouteKey, switchFilter);
    return;
  }

  const referrerUrl = getSameOriginUrl(document.referrer);
  if (!referrerUrl) return;

  const referrerRouteKey = getSwitchModuleRouteKeyFromParsedUrl(referrerUrl);
  const referrerFilter = referrerUrl.searchParams.get(SWITCH_MODULE_FILTER_PARAM);
  const normalizedReferrerFilter = normalizeSwitchModuleFilterValue(referrerFilter);

  if (referrerRouteKey !== currentRouteKey) return;
  if (normalizedCurrentFilter) return;
  if (normalizedReferrerFilter !== normalizedSwitchFilter) return;

  suppressSwitchModuleRedirect(currentRouteKey, switchFilter);
}

function redirectCurrentCollectionWithSwitchFilterIfNeeded(context = null) {
  const resolvedContext = context || getSwitchModuleCollectionContext();
  if (!resolvedContext) return false;

  const currentUrl = new URL(window.location.href);
  const previousUrlFromSession = readSwitchModuleLastUrl();
  if (!isManagedSwitchModuleUrl(currentUrl, resolvedContext)) return false;

  syncSwitchModuleRedirectSuppression(currentUrl, resolvedContext.switchFilter);
  syncGlobalSwitchModuleRedirectSuppression(currentUrl, resolvedContext, previousUrlFromSession);

  const currentRouteKey = getSwitchModuleRouteKeyFromParsedUrl(currentUrl);
  const existingFilter = currentUrl.searchParams.get(SWITCH_MODULE_FILTER_PARAM);
  if (existingFilter) return false;

  if (isSwitchModuleRedirectSuppressed(currentRouteKey, resolvedContext.switchFilter)) {
    return false;
  }

  if (isSwitchModuleRedirectGloballySuppressed(resolvedContext.switchFilter)) {
    return false;
  }

  currentUrl.searchParams.delete(SWITCH_MODULE_LEGACY_FILTER_PARAM);
  currentUrl.searchParams.set(SWITCH_MODULE_FILTER_PARAM, resolvedContext.switchFilter);

  const redirectUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;

  window.location.replace(redirectUrl);
  return true;
}
// end replace url functionality


function getCollectionHandleFromUrl(url) {
  if (!url) return '';

  return getCollectionHandleFromParsedUrl(getParsedSwitchModuleUrl(url));
}

function getCollectionHandleFromParsedUrl(parsedUrl) {
  if (!parsedUrl) return '';

  try {
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
    const collectionsIndex = pathParts.indexOf('collections');

    if (collectionsIndex === -1 || !pathParts[collectionsIndex + 1]) return '';

    return normalizeSwitchModuleCollectionHandle(pathParts[collectionsIndex + 1]);
  } catch (error) {
    return '';
  }
}

function replaceMatchingCollectionUrlsWithSwitchFilter(scope = document) {
  const context = getSwitchModuleCollectionContext();
  if (!context) return;

  const switchFilter = context.switchFilter;
  const queryScope = scope && typeof scope.querySelectorAll === 'function' ? scope : document;

  if (!context.collectionHandles.size && !context.managedUrlTargets.size) return;

  queryScope.querySelectorAll('a[href]').forEach((link) => {
    if (link.classList.contains('facet-clear-all')) {
      return;
    }

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
      return;
    }

    let parsedUrl;

    try {
      parsedUrl = new URL(href, window.location.origin);
    } catch (error) {
      return;
    }

    if (!isManagedSwitchModuleUrl(parsedUrl, context)) return;

    parsedUrl.searchParams.delete(SWITCH_MODULE_LEGACY_FILTER_PARAM);
    parsedUrl.searchParams.set(SWITCH_MODULE_FILTER_PARAM, switchFilter);

    const rewrittenUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;

    link.setAttribute('href', rewrittenUrl);
  });
}

window.replaceMatchingCollectionUrlsWithSwitchFilter = replaceMatchingCollectionUrlsWithSwitchFilter;

document.addEventListener('DOMContentLoaded', function () {
  const switchModuleCollectionContext = getSwitchModuleCollectionContext();
  installSwitchModuleRedirectSuppressionObservers(switchModuleCollectionContext);

  if (redirectCurrentCollectionWithSwitchFilterIfNeeded(switchModuleCollectionContext)) return;

  replaceMatchingCollectionUrlsWithSwitchFilter();

  const overlay = document.querySelector('.switch-module-overlay');
  const modal = document.querySelector('.switch-module');
  const closeButton = document.querySelector('.switch-module__close-button');
  const menuContainer = document.querySelector('.switch-module [data-menu-container]');

  if (!overlay || !modal) return;

  const nav = menuContainer ? new SwitchModuleNavigation(menuContainer) : null;
  let lastClickedButton = null;

  function openModal() {
    document.body.classList.add('switch-modal-open');
    overlay.classList.remove('hidden');
    modal.classList.remove('hidden');

    requestAnimationFrame(() => {
      overlay.classList.add('open');
      modal.classList.add('open');
    });
  }

  function closeModal() {
    overlay.classList.remove('open');
    modal.classList.remove('open');

    setTimeout(() => {
      overlay.classList.add('hidden');
      modal.classList.add('hidden');
      document.body.classList.remove('switch-modal-open');
      if (nav) nav.reset();
      if (lastClickedButton) lastClickedButton.focus();
    }, 300);
  }

  document.addEventListener('click', (e) => {
    const triggerButton = e.target.closest('.choose__watch');
    if (!triggerButton) return;

    e.preventDefault();
    lastClickedButton = triggerButton;
    openModal();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });

  if (closeButton) closeButton.addEventListener('click', () => closeModal());

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) closeModal();
  });
});
